import { Badge } from '@neo/primitives/badge'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import { AlertCircle, ChevronRight, Info, Loader2, MessageSquareOff } from 'lucide-react'
import { useState } from 'react'
import { FlowNodeIconClass } from '../../config/panel.config'
import { useFlowStore } from '../../store'
import type { RResponseItem, RToolCall, RTranscriptTurn } from '../../tabs/simulation-tab.queries'
import type { FlowNodeTypes } from '../../types'
import type { SimulationTurn } from './simulation-store'

// --- Types ---

interface GoalResult {
  goal_name: string
  flow_goal_id: number
  achieved: boolean
  reason: string
  technical_reason: string
}

export type TranscriptTurn = RTranscriptTurn

export type ScenarioMockInputsMap = Record<string, unknown>

interface TurnGroup {
  groupKey: string
  nodeUuid: string
  nodeName: string
  turns: Array<SimulationTurn | TranscriptTurn>
}

// --- Helpers ---

const resolveNodeName = (nodeUuid: string): string => {
  if (!nodeUuid) return 'Unknown'
  const node = useFlowStore.getState().flowInstance?.getNode(nodeUuid)
  return node?.data.config.name || nodeUuid.slice(0, 8)
}

const resolveNodeType = (nodeUuid: string): FlowNodeTypes | null => {
  if (!nodeUuid) return null
  const node = useFlowStore.getState().flowInstance?.getNode(nodeUuid)
  return (node?.type as FlowNodeTypes) ?? null
}

const normalizeNodeUuid = (turn: SimulationTurn | TranscriptTurn): string =>
  'nodeUuid' in turn ? turn.nodeUuid : (turn as TranscriptTurn).node_uuid

const normalizeIsInterruption = (turn: SimulationTurn | TranscriptTurn): boolean =>
  'isInterruption' in turn ? turn.isInterruption : (turn as TranscriptTurn).is_interruption

const normalizePartialMsg = (turn: SimulationTurn | TranscriptTurn): string =>
  'partialAssistantMsg' in turn
    ? turn.partialAssistantMsg
    : ((turn as TranscriptTurn).partial_assistant_msg ?? '')

const normalizeToolCalls = (turn: SimulationTurn | TranscriptTurn): RToolCall[] =>
  'toolCalls' in turn ? turn.toolCalls : ((turn as TranscriptTurn).tool_calls ?? [])

const normalizeResponseItems = (turn: SimulationTurn | TranscriptTurn): RResponseItem[] =>
  'responseItems' in turn
    ? (turn.responseItems ?? [])
    : ((turn as TranscriptTurn).response_items ?? [])

const normalizeVariablesByNode = (
  turn: SimulationTurn | TranscriptTurn,
): Record<string, Record<string, unknown>> =>
  'variablesByNode' in turn
    ? turn.variablesByNode
    : ((turn as TranscriptTurn).variables_by_node ?? {})

const parseJsonLikeValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value
    try {
      return parseJsonLikeValue(JSON.parse(value))
    } catch {
      return value
    }
  }
  if (Array.isArray(value)) return value.map(parseJsonLikeValue)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [
        key,
        parseJsonLikeValue(val),
      ]),
    )
  }
  return value
}

const formatJsonValue = (value: unknown): string => {
  const parsed = parseJsonLikeValue(value)
  if (typeof parsed === 'string') return parsed
  return JSON.stringify(parsed, null, 2) ?? String(parsed)
}

const formatInlineValue = (value: unknown): string => {
  const parsed = parseJsonLikeValue(value)
  if (typeof parsed === 'string') return parsed
  return JSON.stringify(parsed) ?? String(parsed)
}

const hasEntries = (value?: Record<string, unknown> | null): value is Record<string, unknown> =>
  !!value && Object.keys(value).length > 0

const groupTurnsByNode = (turns: Array<SimulationTurn | TranscriptTurn>): TurnGroup[] => {
  const groups: TurnGroup[] = []
  const nodeVisitCounts = new Map<string, number>()
  for (const turn of turns) {
    const nodeUuid = normalizeNodeUuid(turn)
    const last = groups[groups.length - 1]
    if (last && last.nodeUuid === nodeUuid) {
      last.turns.push(turn)
    } else {
      const visitCount = (nodeVisitCounts.get(nodeUuid) ?? 0) + 1
      nodeVisitCounts.set(nodeUuid, visitCount)
      groups.push({
        groupKey: `${nodeUuid || 'unknown'}-${visitCount}`,
        nodeUuid,
        nodeName: resolveNodeName(nodeUuid),
        turns: [turn],
      })
    }
  }
  return groups
}

// --- Score tier system ---

export type Tier = 'high' | 'medium' | 'low' | 'critical'

export const scoreToTier = (score: number): Tier => {
  if (score >= 0.8) return 'high'
  if (score >= 0.5) return 'medium'
  if (score >= 0.25) return 'low'
  return 'critical'
}

export const TIER_CONFIG: Record<Tier, { label: string; className: string }> = {
  high: { label: 'High', className: 'text-status-success' },
  medium: { label: 'Medium', className: 'text-status-warning' },
  low: { label: 'Low', className: 'text-destructive' },
  critical: { label: 'Critical', className: 'text-destructive' },
}

const DETECTION_TIER_CONFIG: Record<Tier, { label: string; className: string }> = {
  high: { label: 'None', className: 'text-status-success' },
  medium: { label: 'Low', className: 'text-status-warning' },
  low: { label: 'Moderate', className: 'text-destructive' },
  critical: { label: 'High', className: 'text-destructive' },
}

const getMetricScore = (metric: Record<string, unknown>): number => (metric.score as number) ?? 1

const getIntentScore = (i: Record<string, unknown>): number =>
  (i.score as number) ??
  (!(i.intent_not_found as boolean) && !(i.intent_wrongly_identified as boolean) ? 1 : 0)

const METRIC_WEIGHTS = {
  intent_identification: 0.3,
  hallucination: 0.25,
  instructions_adherence: 0.2,
  variable_extraction: 0.15,
  node_loop: 0.1,
} as const

const WEIGHTED_METRICS = [
  {
    key: 'intent_identification',
    weight: METRIC_WEIGHTS.intent_identification,
    getScore: getIntentScore,
  },
  { key: 'hallucination', weight: METRIC_WEIGHTS.hallucination, getScore: getMetricScore },
  {
    key: 'instructions_adherence',
    weight: METRIC_WEIGHTS.instructions_adherence,
    getScore: getMetricScore,
  },
  {
    key: 'variable_extraction',
    weight: METRIC_WEIGHTS.variable_extraction,
    getScore: getMetricScore,
  },
  { key: 'node_loop', weight: METRIC_WEIGHTS.node_loop, getScore: getMetricScore },
] as const

// --- Eval scoring ---

interface NodeEvalScore {
  averageScore: number
  scorePercent: number
  tier: Tier
}

const getNodeEvalScore = (node: Record<string, unknown>): NodeEvalScore => {
  let weightedSum = 0
  let totalWeight = 0
  for (const { key, weight, getScore } of WEIGHTED_METRICS) {
    const m = node[key] as Record<string, unknown> | undefined
    if (m) {
      weightedSum += getScore(m) * weight
      totalWeight += weight
    }
  }
  const avg = totalWeight > 0 ? weightedSum / totalWeight : 1
  return { averageScore: avg, scorePercent: Math.round(avg * 100), tier: scoreToTier(avg) }
}

export const getGoalScorePercent = (evaluation: Record<string, unknown>): number | null => {
  const goalEval = evaluation.goal_evaluation as Record<string, unknown> | undefined
  const goals = goalEval?.goals as GoalResult[] | undefined
  if (!goals?.length) return null
  const achieved = goals.filter((g) => g.achieved).length
  return Math.round((achieved / goals.length) * 100)
}

export const getGoalAchieved = (evaluation: Record<string, unknown>): boolean | null => {
  const goalEval = evaluation.goal_evaluation as Record<string, unknown> | undefined
  const goals = goalEval?.goals as GoalResult[] | undefined
  if (!goals?.length) return null
  return goals.some((g) => g.achieved)
}

export const getNodeScorePercent = (evaluation: Record<string, unknown>): number | null => {
  const nodeEvals = evaluation.node_evaluations as Array<Record<string, unknown>> | undefined
  if (!nodeEvals?.length) return null
  let totalWeightedScore = 0
  let totalWeight = 0
  for (const node of nodeEvals) {
    const score = getNodeEvalScore(node)
    const turnCount = (node.turn_count as number) || 1
    totalWeightedScore += score.averageScore * turnCount
    totalWeight += turnCount
  }
  if (totalWeight === 0) return null
  return Math.round((totalWeightedScore / totalWeight) * 100)
}

export const getScorePercent = (evaluation?: Record<string, unknown> | null): number | null => {
  if (!evaluation) return null
  return getGoalScorePercent(evaluation) ?? getNodeScorePercent(evaluation)
}

export type ScoreType = 'goal' | 'node'

export const getScoreType = (evaluation?: Record<string, unknown> | null): ScoreType | null => {
  if (!evaluation) return null
  if (getGoalScorePercent(evaluation) != null) return 'goal'
  if (getNodeScorePercent(evaluation) != null) return 'node'
  return null
}

export const scoreColorClass = (pct: number): string => {
  if (pct >= 80) return 'text-status-success'
  if (pct >= 50) return 'text-status-warning'
  return 'text-status-danger'
}

const STOP_REASON_LABELS: Record<string, string> = {
  end_conversation: 'Ended normally',
  max_turns: 'Max turns reached',
  unknown_intent: 'Unknown intent',
  no_matching_edge: 'No matching edge',
  unsupported_node_type: 'Unsupported node',
  error: 'Error',
}

// --- Turn messages ---

const InterruptedAgentMessage = ({
  fullMessage,
  partialMessage,
}: {
  fullMessage: string
  partialMessage: string
}) => {
  const [showFull, setShowFull] = useState(false)

  return (
    <span>
      <span className="text-s-400">{showFull ? fullMessage : partialMessage}</span>
      <button
        type="button"
        className="text-s-400 text-link hover:underline ml-1"
        data-testid="button-toggle-full-response"
        onClick={() => setShowFull((v) => !v)}
      >
        {showFull ? 'Show as heard' : 'Show full response'}
      </button>
    </span>
  )
}

const TurnMessages = ({
  turn,
  interruptedByPartial,
}: {
  turn: SimulationTurn | TranscriptTurn
  interruptedByPartial?: string
}) => {
  const isInterruption = normalizeIsInterruption(turn)
  const responseItems = normalizeResponseItems(turn)
  const hasOrderedAssistantMessages = responseItems.some(
    (item) => item.type === 'message' && item.role === 'assistant' && item.content,
  )
  const lastAssistantMessageIndex = responseItems.reduce((lastIndex, item, index) => {
    if (item.type === 'message' && item.role === 'assistant' && item.content) return index
    return lastIndex
  }, -1)
  const responseItemKeyCounts = new Map<string, number>()
  const nextResponseItemKey = (base: string): string => {
    const count = (responseItemKeyCounts.get(base) ?? 0) + 1
    responseItemKeyCounts.set(base, count)
    return count === 1 ? base : `${base}-${count}`
  }

  return (
    <>
      {turn.user && (
        <p className="text-s-400">
          <span className="text-s-600">User: </span>
          {turn.user}
          {isInterruption && (
            <span className="text-xs-400 text-status-warning bg-status-warning/10 px-1.5 py-0.5 rounded inline-flex items-center gap-1 ml-1 align-middle">
              <MessageSquareOff className="h-2.5 w-2.5" />
              Interrupted
            </span>
          )}
        </p>
      )}
      {hasOrderedAssistantMessages ? (
        responseItems.map((item, index) => {
          if (item.type === 'message' && item.role === 'assistant' && item.content) {
            return (
              <p
                key={nextResponseItemKey(`message-${item.role}-${item.content}`)}
                className="text-s-400"
              >
                <span className="text-s-600">Agent: </span>
                {interruptedByPartial && index === lastAssistantMessageIndex ? (
                  <InterruptedAgentMessage
                    fullMessage={item.content}
                    partialMessage={interruptedByPartial}
                  />
                ) : (
                  item.content
                )}
              </p>
            )
          }

          if (item.type === 'tool_call' && item.tool_call && isMockedActionCall(item.tool_call)) {
            return (
              <MockedActionCall
                key={nextResponseItemKey(
                  `tool-${item.tool_call.id || item.tool_call.name || item.tool_call.arguments || 'call'}`,
                )}
                call={item.tool_call}
                index={index}
              />
            )
          }

          return null
        })
      ) : (
        <>
          <MockedActionCalls turn={turn} />
          {turn.agent && (
            <p className="text-s-400">
              <span className="text-s-600">Agent: </span>
              {interruptedByPartial ? (
                <InterruptedAgentMessage
                  fullMessage={turn.agent}
                  partialMessage={interruptedByPartial}
                />
              ) : (
                turn.agent
              )}
            </p>
          )}
        </>
      )}
    </>
  )
}

const ChosenPathBox = ({ intent }: { intent: string }) => (
  <div className="flex items-center gap-2 border border-dashed border-tertiary py-2 px-3 rounded-lg mx-0.5 mb-0.5">
    <span className="text-xs-400 text-secondary">Chosen path:</span>
    <span className="text-xs-500 text-primary">{intent}</span>
  </div>
)

const JsonBlock = ({ value }: { value: unknown }) => (
  <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-background px-2 py-1.5 text-xs-400 text-secondary">
    {formatJsonValue(value)}
  </pre>
)

const InfoTooltip = ({ content }: { content: string }) => (
  <Tooltip content={content}>
    <Info className="h-3.5 w-3.5 shrink-0 text-tertiary" />
  </Tooltip>
)

const toTestIdPart = (value: unknown): string =>
  String(value ?? 'item')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

const JsonDetails = ({ label, value }: { label: string; value: unknown }) => {
  const testIdPart = toTestIdPart(label) || 'item'
  return (
    <details className="group" data-testid={`details-json-${testIdPart}`}>
      <summary
        className="text-xs-500 text-secondary cursor-pointer select-none list-none flex items-center gap-1"
        data-testid={`button-json-trigger-${testIdPart}`}
      >
        <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
        {label}
      </summary>
      <div className="mt-1">
        <JsonBlock value={value} />
      </div>
    </details>
  )
}

const isMockedActionCall = (call: RToolCall): boolean =>
  call.tool_type === 'action' && call.mocked === true

const getMockedActionCalls = (turn: SimulationTurn | TranscriptTurn): RToolCall[] =>
  normalizeToolCalls(turn).filter(isMockedActionCall)

const MockedActionCall = ({ call, index }: { call: RToolCall; index: number }) => {
  const testIdPart = toTestIdPart(call.id || call.name || index) || String(index)
  return (
    <details
      className="group rounded-md border border-st2 bg-background px-3 py-2"
      data-testid={`details-mocked-action-${testIdPart}`}
    >
      <summary
        className="flex items-center gap-2 min-w-0 cursor-pointer select-none list-none"
        data-testid={`button-mocked-action-trigger-${testIdPart}`}
      >
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-secondary transition-transform group-open:rotate-90" />
        <span className="text-xs-600 text-primary truncate">{call.name || 'AI Action'}</span>
        <Badge variant="outline" className="text-xs-400 shrink-0">
          Mocked AI Action
        </Badge>
        <InfoTooltip content="This action used the scenario's mock response. No real integration was called." />
        {call.is_error && <span className="text-xs-500 text-destructive shrink-0">Error</span>}
      </summary>
      <div className="mt-2 flex flex-col gap-2 pl-5">
        {call.arguments && call.arguments !== '{}' && (
          <JsonDetails label="Args" value={call.arguments} />
        )}
        {'output' in call && <JsonDetails label="Mock result" value={call.output} />}
      </div>
    </details>
  )
}

const MockedActionCalls = ({ turn }: { turn: SimulationTurn | TranscriptTurn }) => {
  const calls = getMockedActionCalls(turn)
  if (!calls.length) return null

  return (
    <div className="flex flex-col gap-2">
      {calls.map((call, index) => (
        <MockedActionCall key={call.id || `${call.name}-${index}`} call={call} index={index} />
      ))}
    </div>
  )
}

const VariablesBox = ({ variables }: { variables: Record<string, unknown> }) => {
  if (!hasEntries(variables)) return null
  return (
    <details
      className="group mx-0.5 mb-0.5 rounded-md bg-background px-3 py-2"
      data-testid="details-extracted-variables"
    >
      <summary
        className="text-xs-600 text-primary cursor-pointer select-none list-none flex items-center justify-between"
        data-testid="button-extracted-variables-trigger"
      >
        <span className="flex min-w-0 items-center gap-1.5">
          Extracted Variables
          <InfoTooltip content="Values extracted by the AI for this node during the simulation." />
        </span>
        <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
      </summary>
      <div className="mt-2">
        <JsonBlock value={variables} />
      </div>
    </details>
  )
}

const normalizeEvidenceKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, '')

const getMockedActionVariableKeys = (group: TurnGroup): Set<string> => {
  const keys = new Set<string>()
  for (const turn of group.turns) {
    for (const call of getMockedActionCalls(turn)) {
      for (const key of [call.name, call.mock_source_key]) {
        if (key) keys.add(normalizeEvidenceKey(key))
      }
    }
  }
  return keys
}

const removeMockedActionOutputVariables = (
  variables: Record<string, unknown>,
  group: TurnGroup,
): Record<string, unknown> => {
  const mockedActionKeys = getMockedActionVariableKeys(group)
  if (!mockedActionKeys.size) return variables
  return Object.fromEntries(
    Object.entries(variables).filter(([key]) => !mockedActionKeys.has(normalizeEvidenceKey(key))),
  )
}

const getGroupVariables = (group: TurnGroup): Record<string, unknown> => {
  let sawNodeScopedVariables = false

  for (let i = group.turns.length - 1; i >= 0; i--) {
    const variablesByNode = normalizeVariablesByNode(group.turns[i])
    if (!hasEntries(variablesByNode)) continue

    sawNodeScopedVariables = true
    const variables = variablesByNode[group.nodeUuid]
    if (hasEntries(variables)) return removeMockedActionOutputVariables(variables, group)
  }

  if (sawNodeScopedVariables) return {}

  for (let i = group.turns.length - 1; i >= 0; i--) {
    const variables = group.turns[i].variables
    if (hasEntries(variables)) return removeMockedActionOutputVariables(variables, group)
  }
  return {}
}

export const ScenarioMockInputsSummary = ({
  inputs,
}: {
  inputs?: ScenarioMockInputsMap | null
}) => {
  if (!hasEntries(inputs)) return null
  const entries = Object.entries(inputs).filter(([, value]) => value != null)
  if (!entries.length) return null

  return (
    <details
      open
      className="group mt-3 rounded-md border border-st2 bg-background"
      data-testid="details-scenario-mock-inputs"
    >
      <summary
        className="flex cursor-pointer select-none list-none items-center gap-1.5 px-3 py-2 text-xs-600 text-primary"
        data-testid="button-scenario-mock-inputs-trigger"
      >
        <ChevronRight className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90" />
        <span>Scenario Mock Inputs</span>
        <InfoTooltip content="Mock inputs used to start this simulation, such as trigger payload fields and caller details." />
      </summary>
      <dl className="border-t border-st1 px-3 py-2">
        {entries.map(([key, value]) => (
          <div key={key} className="grid grid-cols-[112px_minmax(0,1fr)] gap-2 py-1">
            <dt className="truncate text-xs-400 text-secondary">{key}</dt>
            <dd className="min-w-0 break-words text-xs-500 text-primary">
              {formatInlineValue(value)}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  )
}

// --- Node header ---

const NodeGroupHeader = ({ nodeName, nodeUuid }: { nodeName: string; nodeUuid: string }) => {
  const nodeType = resolveNodeType(nodeUuid)
  const nodeConfig = nodeType ? FlowNodeIconClass[nodeType] : null

  return (
    <div className="flex items-center gap-2 px-3 py-2.5 bg-bg2 rounded-t-md">
      {nodeConfig && (
        <div className={`flex h-6 w-6 items-center justify-center rounded ${nodeConfig.class}`}>
          {nodeConfig.icon}
        </div>
      )}
      <span className="text-p-600 text-primary">{nodeName}</span>
    </div>
  )
}

// --- Node transition (PathRenderer pattern) ---

const NodeTransition = ({ intent }: { intent: string }) => (
  <div className="relative py-4">
    <div className="absolute left-5 top-0 w-px h-4 border-l border-dashed border-tertiary" />
    <div className="flex items-center gap-2 border border-dashed border-tertiary py-2 px-3 rounded-lg">
      <span className="text-xs-400 text-secondary">Chosen path:</span>
      <span className="text-xs-500 text-primary">{intent || 'Unknown'}</span>
    </div>
    <div className="absolute left-5 bottom-0 w-px h-4 border-l border-dashed border-tertiary" />
  </div>
)

// --- Eval components ---

const TierEvalItem = ({
  label,
  score,
  reason,
  detection,
}: {
  label: string
  score: number
  reason?: string
  detection?: boolean
}) => {
  const [expanded, setExpanded] = useState(false)
  const tier = scoreToTier(score)
  const config = detection ? DETECTION_TIER_CONFIG[tier] : TIER_CONFIG[tier]
  const expandable = !!reason

  return (
    <div>
      <button
        type="button"
        disabled={!expandable}
        data-testid="button-tier-eval-toggle"
        className="flex items-baseline gap-2 w-full text-left cursor-pointer disabled:cursor-default"
        onClick={() => expandable && setExpanded((v) => !v)}
      >
        <span className="text-s-400 text-secondary">{label}</span>
        <span className={`text-s-600 ${config.className}`}>{config.label}</span>
        {expandable && (
          <ChevronRight
            className={`h-3 w-3 ml-auto self-center text-secondary transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        )}
      </button>
      {expanded && reason && <p className="text-s-400 text-secondary mt-1">{reason}</p>}
    </div>
  )
}

const BinaryEvalItem = ({
  label,
  passed,
  passedLabel,
  failedLabel,
  reason,
}: {
  label: string
  passed: boolean
  passedLabel: string
  failedLabel: string
  reason?: string
}) => {
  const [expanded, setExpanded] = useState(false)
  const expandable = !!reason

  return (
    <div>
      <button
        type="button"
        disabled={!expandable}
        data-testid="button-binary-eval-toggle"
        className="flex items-baseline gap-2 w-full text-left cursor-pointer disabled:cursor-default"
        onClick={() => expandable && setExpanded((v) => !v)}
      >
        <span className="text-s-400 text-secondary">{label}</span>
        <span className={`text-s-600 ${passed ? 'text-status-success' : 'text-destructive'}`}>
          {passed ? passedLabel : failedLabel}
        </span>
        {expandable && (
          <ChevronRight
            className={`h-3 w-3 ml-auto self-center text-secondary transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        )}
      </button>
      {expanded && reason && <p className="text-s-400 text-secondary mt-1">{reason}</p>}
    </div>
  )
}

const InlineNodeEval = ({ node }: { node: Record<string, unknown> }) => {
  const adherence = node.instructions_adherence as Record<string, unknown> | undefined
  const hallucination = node.hallucination as Record<string, unknown> | undefined
  const intent = node.intent_identification as Record<string, unknown> | undefined
  const loop = node.node_loop as Record<string, unknown> | undefined
  const extraction = node.variable_extraction as Record<string, unknown> | undefined
  const score = getNodeEvalScore(node)

  return (
    <details
      open={score.tier !== 'high'}
      className="group mx-0.5 mb-0.5 px-3 py-3 bg-background rounded-md"
    >
      <summary className="text-s-600 text-primary cursor-pointer select-none list-none flex items-center justify-between hover:text-primary">
        Eval Results
        <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
      </summary>
      <div className="flex flex-col gap-3 mt-3">
        {adherence && (
          <TierEvalItem
            label="Instruction Following"
            score={getMetricScore(adherence)}
            reason={adherence.reason as string}
          />
        )}
        {loop && (
          <TierEvalItem
            label="Loop Detection"
            score={getMetricScore(loop)}
            reason={loop.reason as string}
            detection
          />
        )}
        {hallucination && (
          <TierEvalItem
            label="Hallucination Detection"
            score={getMetricScore(hallucination)}
            reason={hallucination.reason as string}
            detection
          />
        )}
        {intent && (
          <BinaryEvalItem
            label="Intent Detection"
            passed={getIntentScore(intent) >= 0.5}
            passedLabel="Correct"
            failedLabel="Incorrect"
            reason={intent.reason as string}
          />
        )}
        {extraction && (
          <>
            <TierEvalItem
              label="Variable Extraction"
              score={getMetricScore(extraction)}
              reason={extraction.reason as string}
            />
            {(extraction.missing_variables as string[])?.length > 0 && (
              <div className="text-xs-400 text-destructive">
                Missing: {(extraction.missing_variables as string[]).join(', ')}
              </div>
            )}
            {(extraction.incorrect_variables as string[])?.length > 0 && (
              <div className="text-xs-400 text-destructive">
                Incorrect: {(extraction.incorrect_variables as string[]).join(', ')}
              </div>
            )}
          </>
        )}
      </div>
    </details>
  )
}

// --- ErrorBanner ---

const ErrorBanner = ({ message }: { message: string }) => (
  <div className="mx-2 mt-2 flex items-start gap-2 rounded-lg border border-status-danger/30 bg-status-danger/5 px-3 py-2">
    <AlertCircle className="h-4 w-4 text-status-danger shrink-0 mt-0.5" />
    <Typography className="text-s-400 text-status-danger">{message}</Typography>
  </div>
)

// --- Main ConversationFlow ---

interface ConversationFlowProps {
  turns: Array<SimulationTurn | TranscriptTurn>
  isRunning?: boolean
  evaluation?: Record<string, unknown> | null
}

export const ConversationFlow = ({ turns, isRunning, evaluation }: ConversationFlowProps) => {
  const groups = groupTurnsByNode(turns)

  const nodeEvals = evaluation?.node_evaluations as Array<Record<string, unknown>> | undefined
  const evalMap = new Map<string, Record<string, unknown>>()
  if (nodeEvals) {
    for (const ne of nodeEvals) evalMap.set(ne.node_uuid as string, ne)
  }

  const lastGroupIndex = new Map<string, number>()
  groups.forEach((g, i) => {
    lastGroupIndex.set(g.nodeUuid, i)
  })

  return (
    <div className="flex flex-col gap-0 pt-2">
      {groups.map((group, gi) => {
        const nodeEval = evalMap.get(group.nodeUuid)
        const isLastForNode = lastGroupIndex.get(group.nodeUuid) === gi

        return (
          <div key={group.groupKey}>
            {gi > 0 && (
              <NodeTransition
                intent={(() => {
                  const prevTurn = groups[gi - 1].turns[groups[gi - 1].turns.length - 1]
                  return prevTurn?.intent ?? ''
                })()}
              />
            )}
            <div className="border border-st2 rounded-md overflow-hidden">
              <NodeGroupHeader nodeName={group.nodeName} nodeUuid={group.nodeUuid} />
              <div className="flex flex-col gap-4 px-3 py-2.5 bg-bg2">
                {group.turns.map((turn, ti) => {
                  const nextTurn = group.turns[ti + 1]
                  const interruptedByPartial =
                    nextTurn && normalizeIsInterruption(nextTurn)
                      ? normalizePartialMsg(nextTurn)
                      : undefined
                  return (
                    <div key={turn.turn} className="flex flex-col gap-2">
                      <TurnMessages turn={turn} interruptedByPartial={interruptedByPartial} />
                    </div>
                  )
                })}
                <VariablesBox variables={getGroupVariables(group)} />
                {(() => {
                  const groupIntent = (group.turns[group.turns.length - 1]?.intent ?? '').trim()
                  return groupIntent ? <ChosenPathBox intent={groupIntent} /> : null
                })()}
                {nodeEval && isLastForNode && <InlineNodeEval node={nodeEval} />}
              </div>
            </div>
          </div>
        )
      })}

      {isRunning && (
        <div className="flex items-center gap-2 text-s-400 text-secondary mt-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Waiting for turn {turns.length + 1}...</span>
        </div>
      )}
    </div>
  )
}

// --- Cumulative Eval Results ---

const aggregateMetricScores = (nodeEvals: Array<Record<string, unknown>>) => {
  let adhW = 0,
    adhS = 0,
    halW = 0,
    halS = 0,
    extW = 0,
    extS = 0
  let loopW = 0,
    loopS = 0
  let intentPassed = 0,
    intentTotal = 0

  for (const node of nodeEvals) {
    const tc = (node.turn_count as number) || 1
    const a = node.instructions_adherence as Record<string, unknown> | undefined
    if (a) {
      adhS += getMetricScore(a) * tc
      adhW += tc
    }
    const h = node.hallucination as Record<string, unknown> | undefined
    if (h) {
      halS += getMetricScore(h) * tc
      halW += tc
    }
    const v = node.variable_extraction as Record<string, unknown> | undefined
    if (v) {
      extS += getMetricScore(v) * tc
      extW += tc
    }
    const l = node.node_loop as Record<string, unknown> | undefined
    if (l) {
      loopS += getMetricScore(l) * tc
      loopW += tc
    }
    const i = node.intent_identification as Record<string, unknown> | undefined
    if (i) {
      intentTotal++
      if (getIntentScore(i) >= 0.5) intentPassed++
    }
  }

  return {
    adherence: adhW > 0 ? adhS / adhW : 1,
    hallucination: halW > 0 ? halS / halW : 1,
    extraction: extW > 0 ? extS / extW : 1,
    loop: loopW > 0 ? loopS / loopW : 1,
    intentPassed,
    intentTotal,
  }
}

export const CumulativeEvalResults = ({
  evaluation,
}: {
  evaluation?: Record<string, unknown> | null
}) => {
  const nodeEvals = evaluation?.node_evaluations as Array<Record<string, unknown>> | undefined
  if (!nodeEvals?.length) return null

  const metricScores = aggregateMetricScores(nodeEvals)

  const tierMetrics = [
    { label: 'Instruction Following', score: metricScores.adherence, detection: false },
    { label: 'Loop Detection', score: metricScores.loop, detection: true },
    { label: 'Hallucination Detection', score: metricScores.hallucination, detection: true },
    { label: 'Variable Extraction', score: metricScores.extraction, detection: false },
  ]

  const { intentPassed, intentTotal } = metricScores
  const allIntentsCorrect = intentTotal > 0 && intentPassed === intentTotal

  return (
    <div className="mb-4">
      <Typography variant="S/600" className="text-secondary mb-2">
        Cumulative Eval Results
      </Typography>
      <div className="grid grid-cols-2 gap-2">
        {tierMetrics.map((m) => {
          const tier = scoreToTier(m.score)
          const config = m.detection ? DETECTION_TIER_CONFIG[tier] : TIER_CONFIG[tier]
          return (
            <div key={m.label} className="border border-st2 rounded-lg px-3 py-2">
              <Typography variant="S/400" className="text-secondary">
                {m.label}
              </Typography>
              <span className={`text-s-600 ${config.className}`}>{config.label}</span>
            </div>
          )
        })}
        {intentTotal > 0 && (
          <div className="border border-st2 rounded-lg px-3 py-2">
            <Typography variant="S/400" className="text-secondary">
              Intent Detection
            </Typography>
            <span
              className={`text-s-600 ${allIntentsCorrect ? 'text-status-success' : 'text-destructive'}`}
            >
              {intentPassed}/{intentTotal}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// --- GoalEvalResults ---

const GoalItem = ({ goal }: { goal: GoalResult }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border border-st2 rounded-md p-2.5">
      <button
        type="button"
        className="flex items-baseline justify-between w-full text-left cursor-pointer"
        data-testid={`button-goal-item-${goal.flow_goal_id}`}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-s-400 text-secondary">{goal.goal_name}</span>
        <span
          className={`text-s-600 shrink-0 ${goal.achieved ? 'text-status-success' : 'text-destructive'}`}
        >
          {goal.achieved ? 'Achieved' : 'Not Achieved'}
        </span>
      </button>
      {expanded && (
        <div className="mt-1.5 flex flex-col gap-1">
          {goal.reason && <p className="text-xs-400 text-secondary">{goal.reason}</p>}
          {goal.technical_reason && (
            <p className="text-xs-400 text-secondary">{goal.technical_reason}</p>
          )}
        </div>
      )}
    </div>
  )
}

export const GoalEvalResults = ({
  evaluation,
}: {
  evaluation?: Record<string, unknown> | null
}) => {
  const goalEval = evaluation?.goal_evaluation as Record<string, unknown> | undefined
  const goals = goalEval?.goals as GoalResult[] | undefined
  if (!goals?.length) return null

  return (
    <details open className="group mt-3 pt-3 border-t border-st1">
      <summary className="text-xs-600 text-secondary cursor-pointer select-none list-none flex items-center gap-1 hover:text-primary">
        <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
        Goal Results
      </summary>
      <div className="flex flex-col gap-2 mt-2">
        {goals.map((goal) => (
          <GoalItem key={goal.flow_goal_id} goal={goal} />
        ))}
      </div>
    </details>
  )
}

// --- EvalResults ---

export const EvalResults = ({
  stopReason,
  turns,
  error,
}: {
  stopReason: string
  turns: number
  error?: string
}) => (
  <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-st1">
    <div className="flex items-center gap-2 flex-wrap">
      <Badge variant="outline" className="text-xs-400">
        {STOP_REASON_LABELS[stopReason] ?? stopReason}
      </Badge>
      <span className="text-xs-400 text-secondary">{turns} turns</span>
    </div>
    {error && <ErrorBanner message={error} />}
  </div>
)
